import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Input, InputNumber, Modal, Space, Tag } from 'antd';
import { BellOutlined, PauseCircleOutlined, SendOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { errorText } from './helpers';

const STOPPED_BY_BRAKE = ['complaint', 'bounces'];

/** Where the sending stands, starting a wave and stopping it. */
export default function SendControls({ companyId, request, canWrite, onChanged }) {
  const { t } = useTranslation('partnerRequests');
  const [open, setOpen] = useState(false);
  const [wave, setWave] = useState(10);
  const [typed, setTyped] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [typedReminder, setTypedReminder] = useState('');
  const { ready } = request.counts;
  const take = Math.min(wave || 0, ready);
  const braked = STOPPED_BY_BRAKE.includes(request.stopReason);

  useEffect(() => {
    if (open) {
      setTyped('');
      setAcknowledged(false);
      setWave(Math.min(10, ready) || 10);
    }
  }, [open, ready]);

  const start = useMutation({
    mutationFn: () =>
      API.partnerRequests.startWave(companyId, request._id, {
        limit: take,
        confirmCount: parseInt(typed, 10),
        acknowledgeStop: braked && acknowledged,
      }),
    onSuccess: () => {
      setOpen(false);
      onChanged();
    },
  });
  const remind = useMutation({
    mutationFn: () =>
      API.partnerRequests.startReminder(companyId, request._id, {
        confirmCount: parseInt(typedReminder, 10),
        acknowledgeStop: braked && acknowledged,
      }),
    onSuccess: () => {
      setReminding(false);
      setTypedReminder('');
      onChanged();
    },
  });
  const stop = useMutation({
    mutationFn: () => API.partnerRequests.stopSending(companyId, request._id),
    onSuccess: onChanged,
  });

  const stateColor = request.status === 'sending' ? 'processing' : braked ? 'red' : 'default';

  return (
    <>
      <Space wrap className="justify-end">
        <Tag color={stateColor}>{t(`requestStatus.${request.status}`)}</Tag>
        {request.stopReason && <Tag color={braked ? 'red' : 'default'}>{t(`stopReasons.${request.stopReason}`)}</Tag>}
        {request.status === 'sending' && (
          <span className="text-sm opacity-70">{t('send.remaining', { count: request.waveRemaining })}</span>
        )}
        {canWrite && request.status === 'sending' && (
          <Button shape="round" danger icon={<PauseCircleOutlined />} loading={stop.isPending} onClick={() => stop.mutate()}>
            {t('send.stop')}
          </Button>
        )}
        {canWrite && request.status !== 'sending' && request.counts.reminderReady > 0 && (
          <Button shape="round" icon={<BellOutlined />} onClick={() => setReminding(true)}>
            {t('reminder.open', { count: request.counts.reminderReady })}
          </Button>
        )}
        {canWrite && request.status !== 'sending' && (
          <Button shape="round" type="primary" icon={<SendOutlined />} disabled={!ready} onClick={() => setOpen(true)}>
            {t('send.open', { count: ready })}
          </Button>
        )}
      </Space>
      <Modal
        open={reminding}
        maskClosable={false}
        destroyOnClose
        title={t('reminder.title')}
        onCancel={() => setReminding(false)}
        footer={
          <Space>
            <Button shape="round" onClick={() => setReminding(false)}>
              {t('cancel')}
            </Button>
            <Button
              shape="round"
              type="primary"
              danger
              disabled={parseInt(typedReminder, 10) !== request.counts.reminderReady || (braked && !acknowledged)}
              loading={remind.isPending}
              onClick={() => remind.mutate()}
            >
              {t('reminder.submit', { count: request.counts.reminderReady })}
            </Button>
          </Space>
        }
      >
        <div className="grid gap-3">
          {remind.isError && <Alert type="error" showIcon message={errorText(t, remind.error)} />}
          <p>{t('reminder.help', { count: request.counts.reminderReady, days: request.deadlineDays })}</p>
          <div>
            <div className="mb-1 text-sm">{t('send.typeCount', { count: request.counts.reminderReady })}</div>
            <Input value={typedReminder} inputMode="numeric" onChange={(e) => setTypedReminder(e.target.value)} />
          </div>
          {braked && (
            <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
              {t('send.acknowledge')}
            </Checkbox>
          )}
        </div>
      </Modal>
      {stop.isError && <Alert className="mt-2" type="error" showIcon message={errorText(t, stop.error)} />}
      <Modal
        open={open}
        maskClosable={false}
        destroyOnClose
        title={t('send.title')}
        onCancel={() => setOpen(false)}
        footer={
          <Space>
            <Button shape="round" onClick={() => setOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              shape="round"
              type="primary"
              danger
              disabled={parseInt(typed, 10) !== take || !take || (braked && !acknowledged)}
              loading={start.isPending}
              onClick={() => start.mutate()}
            >
              {t('send.submit', { count: take })}
            </Button>
          </Space>
        }
      >
        <div className="grid gap-3">
          {braked && <Alert type="error" showIcon message={t(`send.brake.${request.stopReason}`)} />}
          {!request.testValid && <Alert type="warning" showIcon message={t('errors.test_required')} />}
          {start.isError && <Alert type="error" showIcon message={errorText(t, start.error)} />}
          <p>
            {t('send.summary', {
              company: request.companyInfo.name,
              // The date as stored, whatever the reader's time zone.
              asOf: String(request.asOf).slice(0, 10).split('-').reverse().join('.'),
              ready,
              from: request.fromEmail || '—',
              replyTo: request.replyTo || request.fromEmail || '—',
            })}
          </p>
          <div>
            <div className="mb-1 text-sm">{t('send.waveSize')}</div>
            <InputNumber min={1} max={ready} value={wave} onChange={setWave} className="w-full" />
            <div className="mt-1 text-xs opacity-70">{t('send.pace')}</div>
          </div>
          <div>
            <div className="mb-1 text-sm">{t('send.typeCount', { count: take })}</div>
            <Input value={typed} inputMode="numeric" onChange={(e) => setTyped(e.target.value)} />
          </div>
          {braked && (
            <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
              {t('send.acknowledge')}
            </Checkbox>
          )}
        </div>
      </Modal>
    </>
  );
}
